import { UserRoleEnum } from "@nlc/shared-types";
import { useLocalStorage } from "usehooks-ts";

import { useUser } from "@/hooks/useUser";

import type { PaymentBanner } from "../utils/payment-banners";

import { useSubscriptionOverview } from "../../overview/hooks/useSubscriptionOverview";
import { usePaymentMethods } from "../../payment-methods/hooks/usePaymentMethods";
import { resolvePaymentBanners } from "../utils/payment-banners";

export type PaymentBannerPlacement = "billing" | "requests";

type PaymentBanners = {
  banners: PaymentBanner[];
  canUpdatePaymentMethod: boolean;
  dismiss: (banner: PaymentBanner) => void;
};

const DISMISSED_STORAGE_KEY = "dismissed-payment-banners";

export function usePaymentBanners(
  projectId: number,
  placement: PaymentBannerPlacement,
): PaymentBanners {
  const { data: userData } = useUser();
  const { data: paymentMethods } = usePaymentMethods(projectId);
  const { data: overview } = useSubscriptionOverview(projectId);
  const [dismissed, setDismissed] = useLocalStorage<string[]>(
    DISMISSED_STORAGE_KEY,
    [],
  );

  const roleId = userData?.user.roleId;
  const isOwner = roleId === UserRoleEnum.Owner;
  const isAdmin = roleId === UserRoleEnum.Administrator;

  const banners = resolvePaymentBanners({
    defaultMethod: paymentMethods?.defaultMethod ?? null,
    paymentFailure: overview?.paymentFailure ?? null,
    today: new Date(),
  }).filter((banner) => {
    if (banner.severity === "blocking") return isOwner || isAdmin;

    return (
      isOwner &&
      placement === "billing" &&
      !dismissed.includes(storageKey(projectId, banner))
    );
  });

  const dismiss = (banner: PaymentBanner) =>
    setDismissed((keys) => [...keys, storageKey(projectId, banner)]);

  return { banners, canUpdatePaymentMethod: isOwner, dismiss };
}

function storageKey(projectId: number, banner: PaymentBanner) {
  return `${projectId}:${banner.dismissKey}`;
}
